<template>
	<div class="ventas_tab report_theme_container" data-theme="ventas">
		<!-- ================================================================ -->
		<!-- Header Section                                                    -->
		<!-- ================================================================ -->
		<div class="header_section_header mb-5">
			<div class="header_left">
				<div class="header_title">Ventas</div>
				<div class="header_description mb-4">
					Analiza la tendencia y composición de tus ventas
				</div>

				<!-- Sub-Navigation Filters -->
				<div class="sub_nav_filters">
					<button v-for="period in filterOptions" :key="period.id" class="filter_btn"
						:class="{ 'is-active': filterPeriod === period.id }" @click="updateFilterPeriod(period.id)">
						{{ period.label }}
					</button>
				</div>
			</div>

			<!-- Center: filtros de visualización (Cuentas/Categorías/Clientes) -->
			<div class="center_sub_nav">
				<div class="sub_nav_filters">
					<button v-for="filter in subNavFilters" :key="filter.id" class="filter_btn"
						:class="{ 'is-active': activeSubFilter === filter.id }"
						@click="updateActiveSubFilter(filter.id)">
						{{ filter.label }}
					</button>
				</div>
			</div>

			<!-- Right Actions -->
			<div class="right_actions is-flex">
				<div class="calendar_picker is-flex is-align-items-center mr-3">
					<AnualCalendar class="calendar" :value="endMonth" @onDateChange="updateEndMonth"
						:disableNewerDates="true" :disabledFrom="getNow" formatLabelDate="MMMM YYYY" :alignRight="true" />
				</div>
				<button class="theme_btn_white is-flex is-align-items-center">
					<DownloadFile class="mr-2" style="width: 16px; height: 16px;" />
					Exportar
				</button>
			</div>
		</div>

		<!-- Summary Cards Section -->
		<SalesSummaryCards class="mt-5"
			:monthHeaders="monthHeaders"
			:tableRows="tableRows"
			:totalRow="totalRow" />

		<!-- Main Table Section -->
		<SalesEvolutionTable class="mt-6"
			:monthHeaders="monthHeaders"
			:tableRows="tableRows"
			:totalRow="totalRow"
			:isLoading="isLoading"
			:error="error"
			:selectedPeriod="selectedPeriodRange"
			:periodOptions="periodOptions"
			@change-period="setPeriodRange"
			@retry="fetchSalesData" />
	</div>
</template>

<script>
import moment from "moment";
import { mapState, mapGetters, mapActions, mapMutations } from "vuex";
import AnualCalendar from "@/components/classification/AnualCalendar";
import DownloadFile from "@/components/svg/DownloadFile.vue";
import SalesSummaryCards from "@/components/financialReports/SalesSummaryCards.vue";
import SalesEvolutionTable from "@/components/financialReports/SalesEvolutionTable.vue";
import { PERIOD_TYPES, FILTER_PERIOD_OPTIONS } from "../config/reportStructure";
import { capitalize } from "@/utils/string";

export default {
	name: "VentasTab",

	components: {
		AnualCalendar,
		DownloadFile,
		SalesSummaryCards,
		SalesEvolutionTable,
	},

	data() {
		return {
			periodOptions: PERIOD_TYPES,
			filterOptions: FILTER_PERIOD_OPTIONS,
		};
	},

	computed: {
		...mapState("classification", ["categories"]),
		...mapState("reportsFilters", ["endMonth", "selectedPeriodRange"]),
		...mapState("financialReports", ["filterPeriod"]),
		...mapState("salesReport", [
			"activeSubFilter",
			"monthHeaders",
			"isLoading",
			"error",
		]),
		...mapGetters("salesReport", ["tableRows", "totalRow"]),

		getNow() {
			return moment().toDate();
		},

		subNavFilters() {
			const filters = [{ id: "cuentas", label: "Cuentas" }];
			this.categories.forEach(category => {
				filters.push({ id: category, label: capitalize(category) });
			});
			filters.push({ id: "clientes", label: "Clientes" });
			return filters;
		},

		// Firma reactiva consolidada — un solo trigger para fetch aunque
		// varios filtros cambien en la misma transacción.
		fetchSignature() {
			return [this.endMonth, this.selectedPeriodRange, this.activeSubFilter, this.filterPeriod].join("|");
		},

		// Las summary cards solo dependen del mes de referencia: refetch
		// independiente para no perderlas al cambiar period range o subfilter.
		summarySignature() {
			return moment(this.endMonth).format("YYYY-MM");
		},
	},

	methods: {
		...mapActions("salesReport", ["fetchSalesData", "fetchSalesSummary"]),
		...mapActions("reportsFilters", ["setPeriodRange"]),
		...mapMutations("reportsFilters", ["SET_END_MONTH"]),
		...mapMutations("salesReport", ["SET_ACTIVE_SUB_FILTER"]),
		...mapMutations("financialReports", ["SET_FILTER_PERIOD"]),

		updateEndMonth(date) {
			this.SET_END_MONTH(date);
		},
		updateActiveSubFilter(filter) {
			this.SET_ACTIVE_SUB_FILTER(filter);
		},
		updateFilterPeriod(period) {
			this.SET_FILTER_PERIOD(period);
		},
	},

	mounted() {
		this.fetchSalesData();
		this.fetchSalesSummary();
	},

	watch: {
		fetchSignature() { this.fetchSalesData(); },
		summarySignature() { this.fetchSalesSummary(); },
	},
};
</script>

<style scoped lang="scss">
@import "@/assets/style.scss";

.ventas_tab {
	position: relative;
	padding-bottom: 40px;
	// overflow-y: auto;
	height: 100%;
}

.header_section_header {
	// Layout: 3 zonas en flex con wrap. La zona central crece para llenar
	// el sobrante de su fila (flex:1 1 auto), de modo que:
	//  - en 1 fila: right_actions queda contra el borde derecho y el contenido
	//    de la central queda equidistante de las laterales (≈ space-between).
	//  - si right wrappea a su propia fila: margin-left:auto la ancla a la
	//    derecha; la central sigue ocupando el resto de la fila anterior.
	//  - si cada zona termina en su propia fila: la central llena la fila y
	//    centra su contenido naturalmente.
	display: flex;
	flex-wrap: wrap;
	align-items: flex-end;
	gap: 16px 24px;

	// --tab-sticky-top lo publica DashboardReports midiendo .headerTop con
	// ResizeObserver, así el offset sigue al alto real de la nav (incluso
	// cuando NavigationTabs envuelve a 2 filas en viewports angostos).
	// Fallback 193px ≈ 110 (app header) + 83 (nav en 1 fila) para el primer
	// frame antes de que el observer dispare. z-index intencionalmente menor
	// que el 5 de .headerTop → la nav pinta por encima cuando ambas anclan.
	position: sticky;
	top: var(--tab-sticky-top, 193px);
	z-index: 4;
	background: var(--report-bg);
	border-bottom: 1px solid var(--report-border);
	padding-bottom: 8px;
}

.center_sub_nav,
.right_actions {
	// Estrategia: ambas zonas piden margin-left:auto. En flex, los autos del
	// MISMO eje en items del MISMO renglón comparten el sobrante a partes
	// iguales → en una sola fila, center queda en el midpoint y right en
	// el borde (≡ space-between).
	// Cuando una zona wrappea a su propia fila, el auto de esa fila no tiene
	// con quien repartir y consume TODO el sobrante → la zona queda anclada
	// al borde derecho. Así right_actions siempre se siente "pegada a la
	// derecha" y, cuando center wrappea por sí sola, también va a la derecha.
	margin-left: auto;
}

// Caso degenerado: viewport muy angosto, cada zona termina en su propia fila.
// Pasamos a stack vertical centrado para que las 3 zonas queden alineadas en
// el eje central — coherente con "todo apilado se ve uniforme".
@media (max-width: 600px) {
	.header_section_header {
		flex-direction: column;
		align-items: center;
	}

	.center_sub_nav,
	.right_actions {
		margin-left: 0;
	}

	.header_left {
		display: flex;
		flex-direction: column;
		align-items: center;
		text-align: center;
	}
}

.header_title {
	font-size: 32px;
	font-weight: 700;
	color: var(--report-text);
	margin-bottom: 4px;
}

.header_description {
	font-size: 16px;
	color: var(--report-text-muted);
}

// ------------------------------------------------------------------
// Sub-nav filters (rango de período)
// ------------------------------------------------------------------

.sub_nav_filters {
	display: flex;
	background: var(--report-bg);
	border: 1px solid var(--report-border);
	border-radius: 8px;
	width: fit-content;
}

.filter_btn {
	padding: 6px 16px;
	border: none;
	background: transparent;
	border-radius: 6px;
	font-size: 14px;
	font-weight: 500;
	color: #4b5563;
	cursor: pointer;
	transition: all 0.2s;

	&:hover {
		background: var(--report-surface);
	}

	&.is-active {
		background: var(--report-primary);
		color: #ffffff;
	}
}

// ------------------------------------------------------------------
// Right actions
// ------------------------------------------------------------------

.calendar_picker {
	background: var(--report-primary);
	color: white;
	border-radius: 8px;
	font-weight: 500;

	::v-deep .calendar-label {
		color: white !important;
	}
}

.theme_btn_white {
	background: var(--report-bg);
	border: 1px solid var(--report-border);
	border-radius: 8px;
	padding: 8px 16px;
	font-weight: 500;
	color: #374151;
	box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
	cursor: pointer;

	&:hover {
		background: var(--report-surface);
	}
}
</style>
